import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEmployeeToken } from '../session';
import { KI_ORG_NAME } from '../../config/navigation';
import { useOrganization } from './firm';

/** "Keep Investing", however it has been spelled into an organization record. */
const isKiOrg = (name) => {
  if (!name) return false;
  const v = String(name).trim().toLowerCase().replace(/[^a-z]/g, '');
  return v === KI_ORG_NAME.toLowerCase().replace(/[^a-z]/g, '') || v === 'ki';
};

/**
 * May this session open the Keep Investing staff console?
 *
 * Two conditions, and both are meant:
 *
 *   - the TOKEN says employee. This is the one that matters, because it is the
 *     same claim the backend's `is_employee_context` reads before it will
 *     answer a query for somebody else's client. Anything the app allowed past
 *     this would be a console whose every screen came back refused.
 *   - the ORGANIZATION is Keep Investing, where the account has one at all. An
 *     internal employee usually has no firm membership — they are not an
 *     advisor at a firm — so a missing organization does NOT withhold the
 *     console; only belonging to a DIFFERENT firm does.
 *
 * `loading` matters to callers: the organization arrives a moment after the
 * token, and a gate that read `false` in between would blink the console out
 * of the sidebar on every reload.
 *
 * Two answers come back, and they are not the same question. `allowed` is
 * whether this ACCOUNT may hold the seat — it decides whether the seat is
 * offered and whether a staff token keeps it. `active` is whether the console
 * is in play RIGHT NOW, which is also true when the staff seat has simply been
 * switched to in preview mode. Chrome keys off `active`; anything deciding who
 * gets the seat keys off `allowed`.
 */
export function useEmployeeSeat() {
  const { token, isEmployee } = useSelector((s) => s.auth);
  /* The seat actually being rendered. In the design's test-profile mode nobody
     is signed in and the staff seat is picked from the profile page, so the
     console has to answer to the seat as well as to the token. */
  const role = useSelector((s) => s.ui.role);
  const org = useOrganization();

  return useMemo(() => {
    /* The stored flag is what login worked out; the token is re-read so a
       session restored from storage is judged on the token it actually holds. */
    const staff = Boolean(isEmployee) || isEmployeeToken(token);
    const otherFirm = Boolean(org.name) && !isKiOrg(org.name);
    const allowed = staff && !otherFirm;
    return {
      allowed,
      /* What the chrome should key off: the console is in play either because
         the token grants it, or because this session is running the staff seat
         with no assigned role to stop it. */
      active: allowed || role === 'employee',
      staff,
      loading: org.loading,
      orgName: org.name,
      reason: !staff
        ? 'This account is not a Keep Investing employee.'
        : otherFirm
          ? `This account belongs to ${org.name}, not ${KI_ORG_NAME}.`
          : null,
    };
  }, [isEmployee, token, role, org.name, org.loading]);
}
